import isEqual from 'lodash/isEqual';

import type { LogoDevBrand } from '@/queries';
import {
    defaultDarkTokens,
    defaultLightTokens,
    ensureOpaqueColorContrast,
    fontFamily,
    getColorContrastRatio,
} from '@/theme';
import {
    Whitelabel,
    WhitelabelSharedResourcesSettings,
    WhitelabelThemeTokens,
    WhitelabelWorkspaceThemeSettings,
} from '@/types';

import { extractSocialProfileSlug } from './socialProfiles';

export type ThemeMode = 'light' | 'dark';
export type ThemeTokenKey = keyof WhitelabelThemeTokens;

interface CreateLogoDevWhitelabelImportOptions {
    brand: NonNullable<LogoDevBrand>;
    sharedResources: WhitelabelSharedResourcesSettings;
    website: string;
    workspaceTheme: WhitelabelWorkspaceThemeSettings;
}

export interface LogoDevWhitelabelImport {
    sharedResources: WhitelabelSharedResourcesSettings;
    workspaceTheme: WhitelabelWorkspaceThemeSettings;
}

export const editableTokenKeys: Array<{
    description: string;
    key: ThemeTokenKey;
    label: string;
    type?: 'color' | 'font' | 'number';
}> = [
    {
        key: 'colorPrimary',
        label: 'Primary Color',
        description: 'Primary buttons, selected tabs, and focused controls.',
        type: 'color',
    },
    {
        key: 'colorInfo',
        label: 'Info Color',
        description: 'Informational states, links, and neutral accents.',
        type: 'color',
    },
    {
        key: 'colorSuccess',
        label: 'Success Color',
        description: 'Positive status markers and confirmation states.',
        type: 'color',
    },
    {
        key: 'colorWarning',
        label: 'Warning Color',
        description: 'Attention states and pending status markers.',
        type: 'color',
    },
    {
        key: 'colorError',
        label: 'Error Color',
        description: 'Destructive states and failed validation.',
        type: 'color',
    },
    {
        key: 'colorBgBase',
        label: 'Base Background',
        description: 'Application shell and broad page background.',
        type: 'color',
    },
    {
        key: 'colorBgContainer',
        label: 'Container Background',
        description: 'Cards, panels, modals, and sidebars.',
        type: 'color',
    },
    {
        key: 'colorBgElevated',
        label: 'Elevated Background',
        description: 'Dropdowns, popovers, and layered surfaces.',
        type: 'color',
    },
    {
        key: 'colorTextBase',
        label: 'Text Color',
        description: 'Primary text color for the selected theme.',
        type: 'color',
    },
    {
        key: 'colorBorder',
        label: 'Border Color',
        description: 'Primary borders around controls and panels.',
        type: 'color',
    },
    {
        key: 'colorBorderSecondary',
        label: 'Secondary Border',
        description: 'Subtle dividers and quiet separators.',
        type: 'color',
    },
    {
        key: 'colorSplit',
        label: 'Divider Color',
        description: 'Split lines between related UI regions.',
        type: 'color',
    },
    { key: 'fontFamily', label: 'Font Family', description: 'Typeface used across workspace UI.', type: 'font' },
    {
        key: 'borderRadius',
        label: 'Border Radius',
        description: 'Default corner radius for controls and panels.',
        type: 'number',
    },
];

export const toTokenString = (value: unknown, fallback = '') => (typeof value === 'string' ? value : fallback);

export const pickEditableTokens = (tokens: Record<string, unknown>): WhitelabelThemeTokens =>
    editableTokenKeys.reduce<WhitelabelThemeTokens>((acc, field) => {
        const value = tokens[field.key];

        if (typeof value === 'string' || typeof value === 'number') {
            return { ...acc, [field.key]: value };
        }

        return acc;
    }, {});

export const palettePresets: Array<{
    description: string;
    key: string;
    label: string;
    light: WhitelabelThemeTokens;
    dark: WhitelabelThemeTokens;
}> = [
    {
        key: 'countercyclical',
        label: 'Countercyclical',
        description: 'Clean, neutral, and close to the platform default.',
        light: pickEditableTokens(defaultLightTokens),
        dark: pickEditableTokens(defaultDarkTokens),
    },
    {
        key: 'slate',
        label: 'Slate',
        description: 'A quiet institutional palette with cooler borders.',
        light: {
            ...pickEditableTokens(defaultLightTokens),
            colorPrimary: '#2563eb',
            colorInfo: '#0ea5e9',
            colorBgBase: '#f8fafc',
            colorBgContainer: '#ffffff',
            colorBgElevated: '#ffffff',
            colorTextBase: '#0f172a',
            colorBorder: '#e2e8f0',
            colorBorderSecondary: '#e2e8f0',
            colorSplit: '#e2e8f0',
        },
        dark: {
            ...pickEditableTokens(defaultDarkTokens),
            colorPrimary: '#61afef',
            colorSuccess: '#98c379',
            colorWarning: '#e5c07b',
            colorError: '#e06c75',
            colorInfo: '#56b6c2',
            colorBgBase: '#282c34',
            colorBgContainer: '#21252b',
            colorBgElevated: '#21252b',
            colorBorder: '#3e4451',
            colorBorderSecondary: '#3e4451',
            colorSplit: '#3e4451',
        },
    },
    {
        key: 'pink',
        label: 'Pink',
        description: 'A brighter product-led theme with rose accents.',
        light: {
            ...pickEditableTokens(defaultLightTokens),
            colorPrimary: '#ec4899',
            colorSuccess: '#f9a8d4',
            colorWarning: '#fcd34d',
            colorError: '#f43f5e',
            colorInfo: '#f472b6',
            colorTextBase: '#4a044e',
            colorBgBase: '#ffe4ef',
            colorBgContainer: '#fff0f6',
            colorBgElevated: '#fff0f6',
            colorBorder: '#fbcfe8',
            colorBorderSecondary: '#f9a8d4',
            colorSplit: '#fbcfe8',
        },
        dark: {
            ...pickEditableTokens(defaultDarkTokens),
            colorPrimary: '#f472b6',
            colorInfo: '#fb7185',
            colorSuccess: '#f9a8d4',
            colorWarning: '#fde68a',
            colorError: '#fb7185',
            colorBgBase: '#1f0b17',
            colorBgContainer: '#2a1020',
            colorBgElevated: '#2a1020',
            colorBorder: '#6b2147',
            colorBorderSecondary: '#6b2147',
            colorSplit: '#6b2147',
            colorTextBase: '#fff1f7',
        },
    },
    {
        key: 'market-green',
        label: 'Market Green',
        description: 'Financial workspace greens with soft cool surfaces.',
        light: {
            ...pickEditableTokens(defaultLightTokens),
            colorPrimary: '#217346',
            colorSuccess: '#2e8b57',
            colorWarning: '#e6a23c',
            colorError: '#cc3333',
            colorInfo: '#1e90ff',
            colorTextBase: '#1f2937',
            colorBgBase: '#f0f6ff',
            colorBgContainer: '#ffffff',
            colorBgElevated: '#ffffff',
            colorBorder: '#cbd5e1',
            colorBorderSecondary: '#dbeafe',
            colorSplit: '#dbeafe',
        },
        dark: {
            ...pickEditableTokens(defaultDarkTokens),
            colorPrimary: '#34d399',
            colorSuccess: '#22c55e',
            colorWarning: '#fbbf24',
            colorError: '#f87171',
            colorInfo: '#60a5fa',
            colorBgBase: '#06130d',
            colorBgContainer: '#0b1b13',
            colorBgElevated: '#0b1b13',
            colorBorder: '#16442d',
            colorBorderSecondary: '#16442d',
            colorSplit: '#16442d',
        },
    },
    {
        key: 'retro',
        label: 'Retro',
        description: 'High-contrast terminal styling with neon highlights.',
        light: {
            ...pickEditableTokens(defaultLightTokens),
            colorPrimary: '#16a34a',
            colorSuccess: '#22c55e',
            colorWarning: '#ca8a04',
            colorError: '#dc2626',
            colorInfo: '#0284c7',
            colorBgBase: '#f4f6ef',
            colorBgContainer: '#ffffff',
            colorBgElevated: '#ffffff',
            colorBorder: '#d4d4aa',
            colorBorderSecondary: '#d4d4aa',
            colorSplit: '#d4d4aa',
        },
        dark: {
            ...pickEditableTokens(defaultDarkTokens),
            colorPrimary: '#00ff7f',
            colorSuccess: '#39ff14',
            colorWarning: '#ffd700',
            colorError: '#ff073a',
            colorInfo: '#00bfff',
            colorTextBase: '#d1fae5',
            colorBgBase: '#0a0a0a',
            colorBgContainer: '#000000',
            colorBgElevated: '#000000',
            colorBorder: '#1f2937',
            colorBorderSecondary: '#374151',
            colorSplit: '#1f2937',
        },
    },
];

const defaultWorkspaceTheme: WhitelabelWorkspaceThemeSettings = {
    preset: 'countercyclical',
    reuseLightThemeForDark: true,
    light: palettePresets[0].light,
    dark: palettePresets[0].light,
};

const defaultSharedResources: WhitelabelSharedResourcesSettings = {
    header: {
        brandName: '',
        logoUrl: '',
        showResourceType: true,
        showWorkspaceName: true,
        backgroundColor: '',
        textColor: '',
    },
    content: {
        backgroundColor: '',
        borderColor: '',
        fontFamily,
        linkColor: '',
        maxWidth: 960,
        textColor: '',
    },
    footer: {
        showPoweredBy: true,
        website: '',
        linkedin: '',
        twitter: '',
        supportEmail: '',
        feedbackEmail: '',
    },
};

export const createDefaultWhitelabelSettings = (): LogoDevWhitelabelImport => ({
    sharedResources: {
        content: { ...defaultSharedResources.content },
        footer: { ...defaultSharedResources.footer },
        header: { ...defaultSharedResources.header },
    },
    workspaceTheme: {
        ...defaultWorkspaceTheme,
        dark: { ...defaultWorkspaceTheme.dark },
        light: { ...defaultWorkspaceTheme.light },
    },
});

export const hasWhitelabelCustomizations = (settings: LogoDevWhitelabelImport): boolean => {
    const defaults = createDefaultWhitelabelSettings();

    return (
        !isEqual(settings.workspaceTheme, defaults.workspaceTheme) ||
        !isEqual(settings.sharedResources, defaults.sharedResources)
    );
};

interface ClearLogoDevWhitelabelImportOptions {
    baseline?: LogoDevWhitelabelImport | null;
    sharedResources: WhitelabelSharedResourcesSettings;
    workspaceTheme: WhitelabelWorkspaceThemeSettings;
}

export const clearLogoDevWhitelabelImport = ({
    baseline,
    sharedResources,
    workspaceTheme,
}: ClearLogoDevWhitelabelImportOptions): LogoDevWhitelabelImport => {
    const resetWorkspaceTheme = baseline?.workspaceTheme ?? {
        ...defaultWorkspaceTheme,
        dark: workspaceTheme.reuseLightThemeForDark ? defaultWorkspaceTheme.light : palettePresets[0].dark,
        reuseLightThemeForDark: workspaceTheme.reuseLightThemeForDark,
    };
    const resetSharedResources = baseline?.sharedResources ?? defaultSharedResources;

    return {
        workspaceTheme: {
            ...workspaceTheme,
            dark: {
                ...workspaceTheme.dark,
                colorInfo: resetWorkspaceTheme.dark.colorInfo,
                colorPrimary: resetWorkspaceTheme.dark.colorPrimary,
            },
            light: {
                ...workspaceTheme.light,
                colorInfo: resetWorkspaceTheme.light.colorInfo,
                colorPrimary: resetWorkspaceTheme.light.colorPrimary,
            },
            preset: resetWorkspaceTheme.preset,
        },
        sharedResources: {
            header: {
                ...sharedResources.header,
                backgroundColor: resetSharedResources.header.backgroundColor,
                brandName: resetSharedResources.header.brandName,
                logoUrl: resetSharedResources.header.logoUrl,
                textColor: resetSharedResources.header.textColor,
            },
            content: {
                ...sharedResources.content,
                linkColor: resetSharedResources.content.linkColor,
            },
            footer: {
                ...sharedResources.footer,
                linkedin: resetSharedResources.footer.linkedin,
                twitter: resetSharedResources.footer.twitter,
                website: '',
            },
        },
    };
};

export const mergeWorkspaceTheme = (whitelabel?: Whitelabel | null): WhitelabelWorkspaceThemeSettings => {
    const workspaceTheme = whitelabel?.workspaceTheme;

    if (!workspaceTheme) {
        return createDefaultWhitelabelSettings().workspaceTheme;
    }

    return {
        preset: workspaceTheme.preset || defaultWorkspaceTheme.preset,
        reuseLightThemeForDark: workspaceTheme.reuseLightThemeForDark ?? defaultWorkspaceTheme.reuseLightThemeForDark,
        light: { ...defaultWorkspaceTheme.light, ...workspaceTheme.light },
        dark: { ...defaultWorkspaceTheme.dark, ...workspaceTheme.dark },
    };
};

export const mergeSharedResources = (whitelabel?: Whitelabel | null): WhitelabelSharedResourcesSettings => ({
    header: {
        ...defaultSharedResources.header,
        ...whitelabel?.sharedResources?.header,
        brandName: whitelabel?.sharedResources?.header?.brandName || whitelabel?.name || '',
        logoUrl: whitelabel?.sharedResources?.header?.logoUrl || whitelabel?.image || '',
    },
    content: {
        ...defaultSharedResources.content,
        ...whitelabel?.sharedResources?.content,
    },
    footer: {
        ...defaultSharedResources.footer,
        ...whitelabel?.sharedResources?.footer,
        website: whitelabel?.sharedResources?.footer?.website || whitelabel?.website || '',
        linkedin: whitelabel?.sharedResources?.footer?.linkedin || whitelabel?.linkedin || '',
        twitter: whitelabel?.sharedResources?.footer?.twitter || whitelabel?.twitter || '',
    },
});

const selectAccessibleBrandColor = (
    colors: NonNullable<LogoDevBrand>['colors'],
    backgroundColor: string,
    adjustmentColor: '#000000' | '#ffffff',
    fallbackColor: string,
) => {
    const prominentColors = colors.map(({ hex }) => hex).filter((hex) => /^#[\da-f]{6}$/i.test(hex));

    if (!prominentColors.length) {
        return fallbackColor;
    }

    const accessibleProminentColor = prominentColors.find(
        (color) => getColorContrastRatio(color, backgroundColor) >= 4.5,
    );

    if (accessibleProminentColor) {
        return accessibleProminentColor.toLowerCase();
    }

    return (
        ensureOpaqueColorContrast(prominentColors[0] ?? fallbackColor, backgroundColor, adjustmentColor) ??
        fallbackColor
    );
};

export const createLogoDevWhitelabelImport = ({
    brand,
    sharedResources,
    website,
    workspaceTheme,
}: CreateLogoDevWhitelabelImportOptions): LogoDevWhitelabelImport => {
    const currentLightPrimary = toTokenString(
        workspaceTheme.light.colorPrimary,
        toTokenString(defaultLightTokens.colorPrimary, '#1890ff'),
    );
    const currentDarkPrimary = toTokenString(
        workspaceTheme.dark.colorPrimary,
        toTokenString(defaultDarkTokens.colorPrimary, '#40a9ff'),
    );
    const darkBackground = toTokenString(
        workspaceTheme.dark.colorBgContainer,
        toTokenString(defaultDarkTokens.colorBgContainer, '#141414'),
    );
    const lightPrimary = selectAccessibleBrandColor(brand.colors, '#ffffff', '#000000', currentLightPrimary);
    const darkPrimary = selectAccessibleBrandColor(brand.colors, darkBackground, '#ffffff', currentDarkPrimary);
    const lightTheme = {
        ...workspaceTheme.light,
        colorInfo: lightPrimary,
        colorPrimary: lightPrimary,
    };
    const darkTheme = workspaceTheme.reuseLightThemeForDark
        ? lightTheme
        : {
              ...workspaceTheme.dark,
              colorInfo: darkPrimary,
              colorPrimary: darkPrimary,
          };

    return {
        workspaceTheme: {
            ...workspaceTheme,
            dark: darkTheme,
            light: lightTheme,
            preset: 'brand-import',
        },
        sharedResources: {
            header: {
                ...sharedResources.header,
                backgroundColor: lightPrimary,
                brandName: brand.name || sharedResources.header.brandName,
                logoUrl: brand.logo ?? brand.brandmark ?? sharedResources.header.logoUrl,
                textColor: '#ffffff',
            },
            content: {
                ...sharedResources.content,
                linkColor: lightPrimary,
            },
            footer: {
                ...sharedResources.footer,
                linkedin:
                    extractSocialProfileSlug(brand.socials.linkedin, 'linkedin') || sharedResources.footer.linkedin,
                twitter: extractSocialProfileSlug(brand.socials.twitter, 'twitter') || sharedResources.footer.twitter,
                website: brand.domain || website,
            },
        },
    };
};
